import React, { useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { AlertTriangle, TrendingUp, Lightbulb, MessageCircle, ChevronRight, Lock, Download, Share2 } from 'lucide-react'
import useStore from '../store'

// 雷达图组件
const RadarChart = ({ dimensions }) => {
  const canvasRef = useRef(null)
  const dimArray = Object.values(dimensions)
  const labels = dimArray.map(d => d.label)
  const values = dimArray.map(d => d.score)
  const maxValue = 100

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const dpr = window.devicePixelRatio || 1
    const size = 300
    canvas.width = size * dpr
    canvas.height = size * dpr
    canvas.style.width = size + 'px'
    canvas.style.height = size + 'px'
    ctx.scale(dpr, dpr)

    const centerX = size / 2
    const centerY = size / 2
    const radius = size * 0.35
    const angleStep = (Math.PI * 2) / labels.length

    // 绘制网格
    for (let i = 1; i <= 5; i++) {
      ctx.beginPath()
      ctx.strokeStyle = '#e5e7eb'
      ctx.lineWidth = 1
      for (let j = 0; j < labels.length; j++) {
        const angle = j * angleStep - Math.PI / 2
        const r = (radius / 5) * i
        const x = centerX + Math.cos(angle) * r
        const y = centerY + Math.sin(angle) * r
        if (j === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      }
      ctx.closePath()
      ctx.stroke()
    }

    // 绘制轴线
    for (let i = 0; i < labels.length; i++) {
      const angle = i * angleStep - Math.PI / 2
      ctx.beginPath()
      ctx.strokeStyle = '#e5e7eb'
      ctx.moveTo(centerX, centerY)
      ctx.lineTo(centerX + Math.cos(angle) * radius, centerY + Math.sin(angle) * radius)
      ctx.stroke()

      // 标签
      const labelX = centerX + Math.cos(angle) * (radius + 25)
      const labelY = centerY + Math.sin(angle) * (radius + 25)
      ctx.fillStyle = '#6b7280'
      ctx.font = '12px sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(labels[i], labelX, labelY)
    }

    // 绘制数据区域
    ctx.beginPath()
    ctx.fillStyle = 'rgba(14, 165, 233, 0.2)'
    ctx.strokeStyle = '#0ea5e9'
    ctx.lineWidth = 2
    for (let i = 0; i < values.length; i++) {
      const angle = i * angleStep - Math.PI / 2
      const r = (values[i] / maxValue) * radius
      const x = centerX + Math.cos(angle) * r
      const y = centerY + Math.sin(angle) * r
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    }
    ctx.closePath()
    ctx.fill()
    ctx.stroke()

    // 绘制数据点
    for (let i = 0; i < values.length; i++) {
      const angle = i * angleStep - Math.PI / 2
      const r = (values[i] / maxValue) * radius
      const x = centerX + Math.cos(angle) * r
      const y = centerY + Math.sin(angle) * r
      ctx.beginPath()
      ctx.fillStyle = '#0ea5e9'
      ctx.arc(x, y, 4, 0, Math.PI * 2)
      ctx.fill()
    }
  }, [dimensions])

  return <canvas ref={canvasRef} className="mx-auto" />
}

const ReportPage = () => {
  const navigate = useNavigate()
  const { precisionReport } = useStore()

  if (!precisionReport) {
    return (
      <div className="text-center py-20">
        <AlertTriangle size={48} className="mx-auto text-gray-400 mb-4" />
        <p className="text-gray-600 mb-4">暂无报告数据</p>
        <button onClick={() => navigate('/precision')} className="btn-primary">去生成报告</button>
      </div>
    )
  }

  const { totalScore, dimensions, aiComment, suggestions, foundBadWords, targetPosition } = precisionReport

  const getScoreColor = () => {
    if (totalScore >= 75) return 'text-green-500'
    if (totalScore >= 50) return 'text-primary-500'
    if (totalScore >= 30) return 'text-yellow-500'
    return 'text-red-500'
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* 报告头部 */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card p-6 md:p-8 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">央国企求职精准匹配报告</h1>
            <p className="text-gray-500">目标岗位：{targetPosition} | 生成时间：{new Date(precisionReport.generatedAt).toLocaleString('zh-CN')}</p>
          </div>
          <div className="flex space-x-2 mt-4 md:mt-0">
            <button className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors" title="下载报告">
              <Download size={20} className="text-gray-600" />
            </button>
            <button className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors" title="分享">
              <Share2 size={20} className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* AI评价 */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
          <div className="flex items-center mb-3">
            <AlertTriangle className="text-red-500 mr-2" size={20} />
            <h2 className="text-lg font-bold text-red-700">AI简单粗暴评价</h2>
          </div>
          <p className="text-red-800 leading-relaxed">{aiComment}</p>
        </motion.div>

        {/* 系统评价 */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <div className="flex items-center mb-4">
            <TrendingUp className="text-primary-600 mr-2" size={20} />
            <h2 className="text-lg font-bold text-gray-900">系统综合评价</h2>
          </div>

          <div className="text-center mb-6">
            <div className={`text-5xl font-bold ${getScoreColor()} mb-2`}>{totalScore}分</div>
            <p className="text-gray-500">满分100分</p>
          </div>

          {/* 雷达图 */}
          <div className="flex justify-center mb-6">
            <RadarChart dimensions={dimensions} />
          </div>

          {/* 单项得分 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(dimensions).map(([key, item]) => (
              <div key={key} className="text-center p-3 bg-gray-50 rounded-lg">
                <div className={`text-xl font-bold ${
                  item.score >= 80 ? 'text-green-500' : item.score >= 60 ? 'text-primary-500' : 'text-yellow-500'
                }`}>{item.score}</div>
                <div className="text-xs text-gray-500">{item.label}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* 不符合词汇检测 */}
        {foundBadWords.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-orange-50 border border-orange-200 rounded-xl p-6 mb-6">
            <h3 className="font-bold text-orange-800 mb-2">检测到 {foundBadWords.length} 个不符合央国企文化的词汇</h3>
            <div className="flex flex-wrap gap-2">
              {foundBadWords.map(word => (
                <span key={word} className="px-3 py-1 bg-orange-200 text-orange-800 rounded-full text-sm">{word}</span>
              ))}
            </div>
          </motion.div>
        )}

        {/* 提升建议 */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <div className="flex items-center mb-4">
            <Lightbulb className="text-yellow-500 mr-2" size={20} />
            <h2 className="text-lg font-bold text-gray-900">提升要点与策略</h2>
          </div>
          <div className="space-y-4">
            {suggestions.map((suggestion, index) => (
              <motion.div key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 + index * 0.1 }}
                className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-gray-900">{suggestion.title}</span>
                  <span className={`px-2 py-1 rounded text-xs font-bold ${
                    suggestion.priority === '高' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {suggestion.priority}优先级
                  </span>
                </div>
                <p className="text-gray-600 text-sm">{suggestion.content}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* 顾问引导 */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
          className="bg-gradient-to-r from-primary-600 to-primary-500 rounded-xl p-6 text-white">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
              <MessageCircle size={24} />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg mb-2">需要进一步解读？</h3>
              <p className="text-primary-100 mb-4">你的报告中有3项关键数据需要解读，扫码联系求职顾问看看怎么调整</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/packages')}
                  className="bg-white text-primary-600 font-bold px-6 py-3 rounded-lg hover:bg-primary-50 transition-colors flex items-center justify-center"
                >
                  查看服务套餐<ChevronRight size={18} className="ml-1" />
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* 免责声明 */}
        <div className="mt-6 text-center text-xs text-gray-400 flex items-center justify-center">
          <Lock size={12} className="mr-1" />
          本报告仅供参考，不作为录取保证 | 涉及敏感信息已加密处理
        </div>
      </motion.div>
    </div>
  )
}

export default ReportPage
